/**
 * Service-worker registration via workbox-window.
 *
 * Translates SW lifecycle into simple window events the UI listens for:
 *   - `pwa:downloading`   precache in progress
 *   - `pwa:offline-ready` precache complete — safe to go offline
 *   - `pwa:update-available` a new version is waiting
 *   - `pwa:unsupported`   no service worker support
 */

import { Workbox } from 'workbox-window';

function emit(name: string): void {
  window.dispatchEvent(new CustomEvent(name));
}

export function initPWA(): void {
  if (!('serviceWorker' in navigator)) {
    emit('pwa:unsupported');
    return;
  }
  // The dev server has no service worker (devOptions disabled); skip cleanly.
  if (!import.meta.env.PROD) {
    emit('pwa:offline-ready');
    return;
  }

  // Auto-update: when a newly-deployed worker takes control, reload once so the
  // user gets the fresh app instead of the cached old version. Only arm this
  // when a worker already controls the page (a repeat visit) — on the very
  // first install the initial `controllerchange` must NOT trigger a reload.
  if (navigator.serviceWorker.controller) {
    let reloaded = false;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (reloaded) return;
      reloaded = true;
      window.location.reload();
    });
  }

  const swUrl = `${import.meta.env.BASE_URL}sw.js`;
  const wb = new Workbox(swUrl, { scope: import.meta.env.BASE_URL });

  emit('pwa:downloading');

  wb.addEventListener('installed', (event) => {
    // First install => precache is populated; safe to go offline.
    if (!event.isUpdate) emit('pwa:offline-ready');
  });
  wb.addEventListener('activated', () => emit('pwa:offline-ready'));
  wb.addEventListener('controlling', () => emit('pwa:offline-ready'));
  wb.addEventListener('waiting', () => emit('pwa:update-available'));

  // The SW may also post an explicit readiness message (e.g. after warming
  // the large basemap file).
  navigator.serviceWorker.addEventListener('message', (e: MessageEvent) => {
    const data = e.data as { type?: string } | undefined;
    if (data?.type === 'OFFLINE_READY') emit('pwa:offline-ready');
  });

  wb.register()
    .then(() => {
      // On a repeat visit the compatible SW is already active/controlling, so
      // none of the lifecycle events above fire — but the precache is present
      // from a prior session, so it is already safe to go offline.
      if (navigator.serviceWorker.controller) emit('pwa:offline-ready');
    })
    .catch(() => emit('pwa:unsupported'));

  // Resolves once an active SW controls this scope (covers both first-load
  // activation and every repeat visit) — the robust "precache is live" signal.
  navigator.serviceWorker.ready.then(() => emit('pwa:offline-ready')).catch(() => undefined);
}
